import { useCallback, useMemo, useState } from 'react';
import { compact, sortBy, xor } from 'lodash-es';
import cn from 'classnames';
import {
  TmdxAvatar,
  TmdxAvatarGroup,
  Listbox,
  ListboxItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  Selection,
  Tooltip,
} from '@ds';
import { getFullName } from '@/utils/helpers';
import { BaseResource, BaseShift, ResourcesWithShifts } from '@/api/apis/resources/schedule';

type ResourcesFilterProps<T extends string, R extends BaseResource> = {
  shiftsData: ResourcesWithShifts<R, BaseShift<T>>;
  selectedKeys: string[];
  onSelectedKeysChange: (selectedKeys: string[]) => void;
};

export const SchedulerResourcesFilter = <T extends string, R extends BaseResource>({
  shiftsData,
  selectedKeys,
  onSelectedKeysChange,
}: ResourcesFilterProps<T, R>) => {
  const [searchValue, setSearchValue] = useState('');

  const fullResourceList = useMemo(() => {
    const list = compact(Object.values(shiftsData).map((r) => r.resource));
    return sortBy(list, (r) => getFullName(r.firstName, r.lastName));
  }, [shiftsData]);

  const resourceList = useMemo(() => {
    return searchValue
      ? fullResourceList.filter((r) => {
          const fullName = getFullName(r.firstName, r.lastName);
          return fullName.toLowerCase().includes(searchValue.toLowerCase());
        })
      : fullResourceList;
  }, [searchValue, fullResourceList]);

  const onSelection = useCallback(
    (keys: Selection) => {
      if (keys === 'all') {
        onSelectedKeysChange(resourceList.map((i) => i.id));
        return;
      }
      onSelectedKeysChange(Array.from(keys as Set<string>));
    },
    [onSelectedKeysChange, resourceList],
  );

  const onAvatarSelect = useCallback(
    (userId: string) => onSelectedKeysChange(xor(selectedKeys, [userId])),
    [onSelectedKeysChange, selectedKeys],
  );

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) setSearchValue('');
  }, []);

  return (
    <Popover
      placement="bottom-end"
      size="md"
      triggerType="listbox"
      triggerScaleOnOpen={false}
      offset={8}
      onOpenChange={onOpenChange}
      classNames={{
        content: 'min-w-40 bg-base-60 p-2 rounded-md items-stretch',
      }}
    >
      <TmdxAvatarGroup
        max={4}
        total={fullResourceList.length}
        size="md"
        color="default"
        isBordered
        renderCount={(resourceCount) => (
          <PopoverTrigger>
            <span className="cursor-pointer text-m-r text-base-10 ml-1">+{resourceCount}</span>
          </PopoverTrigger>
        )}
      >
        {fullResourceList.map((r) => (
          <Tooltip
            key={r.id}
            classNames={{
              content: 'px-3 text-nowrap',
            }}
            content={getFullName(r.firstName, r.lastName)}
          >
            <TmdxAvatar
              src={r.profilePhoto}
              name={getFullName(r.firstName, r.lastName)}
              onClick={() => onAvatarSelect(r.id)}
              classNames={{
                base: cn('cursor-pointer', selectedKeys.includes(r.id) ? 'ring-base-5' : 'ring-base-70'),
              }}
            />
          </Tooltip>
        ))}
      </TmdxAvatarGroup>
      <PopoverContent className="max-w-[270px]">
        <div className="mb-2">
          <SearchInput placeholder="Search" value={searchValue} onValueChange={setSearchValue} />
        </div>
        <div className="w-full max-h-56 overflow-auto">
          <Listbox
            hideEmptyContent
            aria-label="Resource List"
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={onSelection}
            classNames={{ base: 'p-0' }}
          >
            {resourceList.map((item) => {
              const fullName = getFullName(item.firstName, item.lastName);

              return (
                <ListboxItem key={item.id} textValue={fullName}>
                  <div className="flex items-center gap-2">
                    <TmdxAvatar src={item.profilePhoto} size="lg" radius="lg" name={fullName} />
                    <div className="flex-1 max-w-[170px]">
                      <div className="text-m-m text-wrap">{fullName}</div>
                    </div>
                  </div>
                </ListboxItem>
              );
            })}
          </Listbox>
        </div>
      </PopoverContent>
    </Popover>
  );
};
